import { type FC, useState, useContext } from 'react';
import classnames from 'classnames';
import { useMutation, graphql } from 'react-relay';
import { FormattedMessage, useIntl, defineMessages } from 'dibs-react-intl';
import { EMAIL_REGEX } from 'dibs-constants/exports/validationRegexes.ts';

import { Button } from 'dibs-elements/exports/Button.tsx';
import { Input } from 'dibs-elements/exports/Input.tsx';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';
import { TextArea } from 'dibs-elements/exports/TextArea.tsx';
import { InlineAlert } from 'dibs-elements/exports/InlineAlert.tsx';
import Contact1stdibsFileUpload from '../FileUpload.tsx';
import { trackSubmit } from '../tracking.ts';

import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';
import { RENDER_AS, ACTIONS, STEPS } from './GetHelpZendeskConstants.ts';

import { type GetHelpZendeskSendMessageFormMutation } from './__generated__/GetHelpZendeskSendMessageFormMutation.graphql.ts';

import dibsCss from 'dibs-css';

type FormData = {
    name: string;
    email: string;
    message: string;
    file: File | null;
    errors: {
        name: '';
        email: '';
        message: '';
    };
};

const isValidEmail = (email: string | undefined): boolean => !!(email && email.match(EMAIL_REGEX));

const messages = defineMessages({
    messagePlaceholder: {
        id: 'getHelp.GetHelpZendeskSendMessageForm.messagePlaceholder',
        defaultMessage: 'Let us know how we can help!',
    },
    nameLabel: {
        id: 'getHelp.GetHelpZendeskSendMessageForm.nameLabel',
        defaultMessage: 'Your Name*',
    },
    emailLabel: {
        id: 'getHelp.GetHelpZendeskSendMessageForm.emailLabel',
        defaultMessage: 'Your Email*',
    },
    emailError: {
        id: 'getHelp.GetHelpZendeskSendMessageForm.emailError',
        defaultMessage: 'Please enter a valid email address',
    },
});

const initialFormData = {
    name: '',
    email: '',
    message: '',
    file: null,
    errors: { name: '', email: '', message: '' },
};

const GetHelpZendeskSendMessageForm: FC<{ topicValue: string }> = ({ topicValue }) => {
    const intl = useIntl();

    const {
        state: { renderAs, orderId, userId, sellerId },
        dispatch,
    } = useContext(GetHelpZendeskContext);

    const [formData, setFormData] = useState(initialFormData);

    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    const { name, email, message, file, errors } = formData;

    const requiredFields: (keyof FormData)[] =
        renderAs === RENDER_AS.LOGGED_OUT ? ['name', 'email', 'message'] : ['message'];

    const setFieldValue = ({
        key,
        value,
    }: {
        key: keyof FormData;
        value?: string | File;
    }): void => {
        setFormData({ ...formData, [key]: value });
    };

    const setError = ({ key, value }: { key: keyof FormData; value: string }): void => {
        setFormData({ ...formData, errors: { ...formData.errors, [key]: value } });
    };

    const [submitForm, isLoading] = useMutation<GetHelpZendeskSendMessageFormMutation>(graphql`
        mutation GetHelpZendeskSendMessageFormMutation($input: CreateContactRequestMutationInput!) {
            CreateContactRequestMutation(input: $input) {
                recordId
            }
        }
    `);

    const validate = (): boolean => {
        let isValid = true;

        if (renderAs === RENDER_AS.LOGGED_OUT) {
            if (!isValidEmail(email)) {
                setError({
                    key: 'email',
                    value: intl.formatMessage(messages.emailError),
                });

                isValid = false;
            }
        }

        return isValid;
    };

    const onSubmit = (): void => {
        if (!validate()) {
            return;
        }

        submitForm({
            variables: {
                input: {
                    orderId,
                    name,
                    email,
                    message,
                    topic: topicValue,
                    buyerId: userId,
                    url: window.location.href,
                    attachment: file,
                    sellerId,
                },
            },
            onCompleted() {
                setShowSuccessMessage(true);
                setFormData(initialFormData);
                trackSubmit(topicValue || '');
                dispatch({ type: ACTIONS.SET_SHOW_FURTHER_SUPPORT, value: false });
                dispatch({ type: ACTIONS.SET_CONTACT_SUPPORT_SEARCH_QUERY, query: '' });
            },
            onError() {
                dispatch({ type: ACTIONS.SET_STEP, step: STEPS.ERROR });
            },
        });
    };

    const isDisabled = !Object.values(
        requiredFields.reduce((acc, field) => ({ ...acc, [field]: formData[field] }), {})
    ).every(value => !!value);

    return (
        <div>
            {showSuccessMessage && (
                <div className={dibsCss.mtMedium}>
                    <InlineAlert variant="success">
                        <FormattedMessage
                            id="getHelp.GetHelpZendeskSendMessageForm.successMessage"
                            defaultMessage="Your message has been sent successfully. A 1stDibs representative will review your message and reach out within <bold>2 business days</bold>."
                            values={{
                                bold: content => (
                                    <span className={dibsCss.sassyFontBodySmallHeavy}>
                                        {content}
                                    </span>
                                ),
                            }}
                        />
                    </InlineAlert>
                </div>
            )}
            {renderAs === RENDER_AS.LOGGED_OUT && (
                <>
                    <div className={dibsCss.mtSmall}>
                        <Input
                            dataTn="get-help-zendesk-send-message-form-name-input"
                            label={intl.formatMessage(messages.nameLabel)}
                            onChange={e =>
                                setFieldValue({ key: 'name', value: e.target.value || '' })
                            }
                            hasError={!!errors.name}
                            errorMessage={errors.name}
                            value={name}
                        />
                    </div>
                    <div className={dibsCss.mtSmall}>
                        <Input
                            dataTn="get-help-send-zendesk-message-form-email-input"
                            label={intl.formatMessage(messages.emailLabel)}
                            onChange={e =>
                                setFieldValue({ key: 'email', value: e.target.value || '' })
                            }
                            hasError={!!errors.email}
                            errorMessage={errors.email}
                            value={email}
                        />
                    </div>
                </>
            )}
            <div className={dibsCss.mtSmall}>
                <TextArea
                    dataTn="get-help-zendesk-send-message-textarea"
                    label={
                        <FormattedMessage
                            id="getHelp.GetHelpZendeskSendMessageForm.messageLabel"
                            defaultMessage="Your Message*"
                        />
                    }
                    maxLength={30000}
                    placeholder={intl.formatMessage(messages.messagePlaceholder)}
                    onChange={e => setFieldValue({ key: 'message', value: e.target.value || '' })}
                    value={message}
                    errorMessage={errors.message}
                />
            </div>
            <Contact1stdibsFileUpload
                onChange={value => setFieldValue({ key: 'file', value })}
                renderAs={renderAs}
                file={file}
            />
            <Button
                dataTn="get-help-zendesk-modal-send-message-cta"
                onClick={() => onSubmit()}
                fullWidth
                disabled={isDisabled}
            >
                <div
                    className={classnames(dibsCss.flex, dibsCss.itemsCenter, dibsCss.justifyCenter)}
                >
                    {isLoading ? (
                        <Spinner
                            size="tiny"
                            containerClass={classnames(dibsCss.flex, dibsCss.justifyCenter)}
                        />
                    ) : (
                        <FormattedMessage
                            id="getHelp.GetHelpZendeskSendMessageForm.sendMessageButton"
                            defaultMessage="send message"
                        />
                    )}
                </div>
            </Button>
        </div>
    );
};

export { GetHelpZendeskSendMessageForm };
